import { LMSAction } from '@nsc/simple-lms/init.js';

export { SmartTeamsAppUserGroup } from '@nsc/smartteams-shared/db/SmartTeamsAppUserGroup.js';

export enum SmartTeamsAppPermission {
  // Ticket Management
  TicketRead = 'TicketRead',
  /** Permission to call status-change mutations. Holding it is necessary but not sufficient — every mutation also enforces the assignee check. */
  TicketUpdate = 'TicketUpdate',
  TicketView = 'TicketView',
  /** Customer-only: submit a review for a ticket they were the caller on. */
  TicketReview = 'TicketReview',
  // User Management
  UserManage = 'UserManage',
}

/**
 * Role names for the SmartTeams App.
 * NSC Admin and Partner Manager are shared with the SmartTeams server.
 * Field Engineer and Customer are app-specific roles.
 */
export const SmartTeamsAppRoleNames = {
  NSC_ADMIN: 'NSC Admin',
  PARTNER_MANAGER: 'Partner Manager',
  FIELD_ENGINEER: 'Field Engineer',
  CUSTOMER: 'Customer',
} as const;

/**
 * Role permissions:
 * - NSC Admin: read-only across all tickets + user management + LMS. Explicitly NOT TicketUpdate or TicketReview.
 * - Field Engineer: ticket read/view/update — but server-side every update mutation re-checks they are the assignee of the ticket.
 * - Customer: ticket read/view + review their own tickets.
 * - Partner Manager: ticket read/view.
 *
 * `permissionsToRemove` is applied with $pullAll on every startup to keep stale permissions from accumulating
 * across iterations (e.g. when we strip TicketUpdate from Admin).
 */
export const SmartTeamsAppRoleAugmentations = {
  [SmartTeamsAppRoleNames.NSC_ADMIN]: {
    roleName: SmartTeamsAppRoleNames.NSC_ADMIN,
    additionalPermissions: [
      SmartTeamsAppPermission.TicketRead,
      SmartTeamsAppPermission.TicketView,
      SmartTeamsAppPermission.UserManage,
      ...(Object.values(LMSAction) as string[]),
    ],
    permissionsToRemove: [SmartTeamsAppPermission.TicketUpdate, SmartTeamsAppPermission.TicketReview],
    createIfMissing: true,
  },
  [SmartTeamsAppRoleNames.FIELD_ENGINEER]: {
    roleName: SmartTeamsAppRoleNames.FIELD_ENGINEER,
    additionalPermissions: [
      SmartTeamsAppPermission.TicketRead,
      SmartTeamsAppPermission.TicketView,
      SmartTeamsAppPermission.TicketUpdate,
    ],
    permissionsToRemove: [SmartTeamsAppPermission.TicketReview],
    createIfMissing: true,
  },
  [SmartTeamsAppRoleNames.CUSTOMER]: {
    roleName: SmartTeamsAppRoleNames.CUSTOMER,
    additionalPermissions: [
      SmartTeamsAppPermission.TicketRead,
      SmartTeamsAppPermission.TicketView,
      SmartTeamsAppPermission.TicketReview,
    ],
    permissionsToRemove: [SmartTeamsAppPermission.TicketUpdate, SmartTeamsAppPermission.UserManage],
    createIfMissing: true,
  },
  [SmartTeamsAppRoleNames.PARTNER_MANAGER]: {
    roleName: SmartTeamsAppRoleNames.PARTNER_MANAGER,
    additionalPermissions: [SmartTeamsAppPermission.TicketRead, SmartTeamsAppPermission.TicketView],
    permissionsToRemove: [SmartTeamsAppPermission.TicketUpdate, SmartTeamsAppPermission.TicketReview],
    createIfMissing: true,
  },
} as const;

/**
 * Route to Permission mapping for the SmartTeams App.
 * Defines which permission is required to access each route.
 */
export const ROUTE_PERMISSION_MAP: Record<string, string> = {
  '/': SmartTeamsAppPermission.TicketView,
  // Colleague tickets read the same ServiceNow data as the FE's own list; company scoping is
  // enforced server-side in api/colleague-ticket-queries.ts.
  '/colleagues': SmartTeamsAppPermission.TicketRead,
  // Coarse gate for the detail deep-link; per-ticket ownership is still enforced server-side.
  '/tickets/detail/:number': SmartTeamsAppPermission.TicketRead,
  '/academy': LMSAction.ViewCourse,
  '/profile': '', // always accessible when logged in
  '/users': SmartTeamsAppPermission.UserManage,
};
